import { BeneficialOwnerControlType } from '@commenda/api';
import {
  OWNERSHIP_ENTITY_RELATIONSHIPS,
  OWNERSHIP_INDIVIDUAL_RELATIONSHIPS,
  OwnershipRelationshipTypeMap,
} from '@commenda/utils';

// Fallback when the BE doesn't return one. Most regimes converge on 25%.
export const DEFAULT_UBO_THRESHOLD_PERCENT = 25;

// 100 = two decimals. Mirrors BE OWNERSHIP_PERCENT_SCALE.
export const DEFAULT_PERCENT_SCALE = 100;

// Form-state marker for a staged, not-yet-created share class: the id doesn't
// exist yet, but the field still counts as filled in for validation. Unwrap it
// (`useStockClassStaging`) before it reaches anything that resolves a real
// class — passed through raw it silently matches nothing.
export const STAGED_STOCK_CLASS_ID = '__staged__';

export const CONTROL_TYPE_LABELS: Record<string, string> = {
  DIRECT_OWNERSHIP: 'Control through direct ownership',
  INDIRECT_OWNERSHIP: 'Control through indirect ownership',
  DIFFERENTIAL_VOTING_RIGHTS: 'Differential voting rights',
  APPOINT_SENIOR_MANAGEMENT: 'Power to appoint the majority of senior management',
  DEBT_INSTRUMENTS: 'Control through debt instruments',
  POSITIONS_WITHIN_LEGAL_PERSON: 'Control through positions held within a legal person',
  ANOTHER_PERSON: 'Control through another person',
  OTHER: 'Other',
};

export const CONTROL_TYPE_CAPTIONS: Record<string, string> = {
  DIRECT_OWNERSHIP:
    'This person directly owns enough of the company to qualify as a beneficial owner under local rules.',
  INDIRECT_OWNERSHIP:
    'This person controls or owns the company through another company or legal entity.',
  DIFFERENTIAL_VOTING_RIGHTS:
    'Some shares carry extra voting power. A person may control the company even if they own a smaller percentage of shares.',
  APPOINT_SENIOR_MANAGEMENT:
    "This person can appoint most of the company's senior leaders or decision-makers, directly or through another entity they control.",
  DEBT_INSTRUMENTS:
    'This person may control the company through a loan, financing arrangement, or similar agreement that gives them significant influence over decisions.',
  POSITIONS_WITHIN_LEGAL_PERSON:
    "This person holds a role that gives them substantial influence over the company's strategy or major decisions.",
  ANOTHER_PERSON:
    'This person controls the company through someone else, such as a nominee, close associate, or other informal arrangement.',
  OTHER: 'This person qualifies as a beneficial owner for another reason.',
};

export const CONTROL_TYPE_OPTIONS: { label: string; value: string }[] = [
  BeneficialOwnerControlType.DIRECT_OWNERSHIP,
  BeneficialOwnerControlType.INDIRECT_OWNERSHIP,
  BeneficialOwnerControlType.OTHER,
].map((value) => ({ value, label: CONTROL_TYPE_LABELS[value] }));

export const INDIVIDUAL_RELATIONSHIP_OPTIONS = OWNERSHIP_INDIVIDUAL_RELATIONSHIPS.map(
  (v) => ({ label: OwnershipRelationshipTypeMap[v], value: v }),
);
export const ENTITY_RELATIONSHIP_OPTIONS = OWNERSHIP_ENTITY_RELATIONSHIPS.map(
  (v) => ({ label: OwnershipRelationshipTypeMap[v], value: v }),
);

// Shuffled so adjacent stacked-bar segments never share a hue family.
// Cycles after 10. Hover is the next-darker step in the same family.
export interface StakeholderColor {
  base: string;
  hover: string;
}

export const STAKEHOLDER_COLORS: readonly StakeholderColor[] = [
  { base: '#2563EB', hover: '#1D4ED8' }, // blue
  { base: '#10B981', hover: '#059669' }, // green
  { base: '#F97316', hover: '#EA580C' }, // orange
  { base: '#8B5CF6', hover: '#7C3AED' }, // purple
  { base: '#0EA5E9', hover: '#0284C7' }, // cyan (light)
  { base: '#EF4444', hover: '#DC2626' }, // red
  { base: '#EAB308', hover: '#CA8A04' }, // yellow
  { base: '#D946EF', hover: '#C026D3' }, // magenta
  { base: '#84CC16', hover: '#65A30D' }, // lime
  { base: '#06B6D4', hover: '#0891B2' }, // teal
] as const;

export const getStakeholderColor = (index: number): StakeholderColor =>
  STAKEHOLDER_COLORS[index % STAKEHOLDER_COLORS.length];

// Sentinel for the synthetic "All other shareholders" row absorbing
// 100 − sum(explicit). Participates in the normal color rotation.
export const OTHERS_STAKEHOLDER_ID = '__others__';

// Stable id → color-index. TanStack's `row.index` is page-relative and
// would desync the bar from row dots under pagination.
export const buildStakeholderColorIndex = (
  ids: ReadonlyArray<{ stakeholdingId: string }>
): Map<string, number> => {
  const map = new Map<string, number>();
  ids.forEach((row, i) => map.set(row.stakeholdingId, i));
  return map;
};

export const getStakeholderColorById = (
  map: ReadonlyMap<string, number>,
  stakeholdingId: string
): StakeholderColor => {
  const idx = map.get(stakeholdingId) ?? 0;
  return getStakeholderColor(idx);
};
